/**
 * Telemetria leve do fluxo de login.
 *
 * Conta:
 *  - quantas vezes cada tela (auth / role-select) é montada
 *  - quantas mudanças de rota ocorrem dentro do fluxo
 *  - device / app version para correlacionar bugs no Android
 *
 * Os contadores ficam em memória + sessionStorage (para sobreviver a
 * redirects de OAuth) e são emitidos via console.info + lovable analytics
 * (quando disponível) para serem capturados pelos logs do dispositivo.
 */



const STORAGE_KEY = "zappy.login-flow-telemetry.v1";
const LOGIN_FLOW_ROUTES = ["/auth", "/role-select", "/age-gate"];

type FlowState = {
  startedAt: number;
  routeChanges: number;
  screenMounts: Record<string, number>;
  routeHistory: string[];
  lastRoute: string | null;
};

const initialState = (): FlowState => ({
  startedAt: Date.now(),
  routeChanges: 0,
  screenMounts: {},
  routeHistory: [],
  lastRoute: null,
});

const isInLoginFlow = (pathname: string): boolean =>
  LOGIN_FLOW_ROUTES.some((p) => pathname === p || pathname.startsWith(`${p}/`));

const readState = (): FlowState | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as FlowState) : null;
  } catch {
    return null;
  }
};

const writeState = (state: FlowState | null) => {
  try {
    if (!state) sessionStorage.removeItem(STORAGE_KEY);
    else sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
};

const getDeviceContext = () => {
  if (typeof navigator === "undefined") return {};
  const ua = navigator.userAgent || "";
  const isAndroid = /Android/i.test(ua);
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isStandalone =
    typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)").matches ||
      // iOS Safari
      (window.navigator as unknown as { standalone?: boolean }).standalone === true);
  return {
    user_agent: ua,
    platform: isAndroid ? "android" : isIOS ? "ios" : "web",
    standalone: !!isStandalone,
    app_version: (import.meta.env.VITE_APP_VERSION as string | undefined) ?? "dev",
  };
};

const emit = (event: string, payload: Record<string, unknown>) => {
  const data = { ...payload, ...getDeviceContext() };
  try {
    // eslint-disable-next-line no-console
    console.info(`[login-flow] ${event}`, data);
  } catch {
    /* ignore */
  }
  try {
    // Dispara um CustomEvent para qualquer telemetria externa que queira
    // se inscrever — mantém este módulo desacoplado de SDKs específicos.
    window.dispatchEvent(new CustomEvent("login-flow-telemetry", { detail: { event, data } }));
  } catch {
    /* ignore */
  }
};

const isPageReload = (): boolean => {
  try {
    const nav = performance.getEntriesByType("navigation")[0] as
      | PerformanceNavigationTiming
      | undefined;
    return nav?.type === "reload";
  } catch {
    return false;
  }
};

export const recordScreenMount = (screenId: string) => {
  const state = readState() ?? initialState();
  // Reload da mesma tela não é uma "segunda montagem real" — o usuário
  // continua na mesma instância lógica do fluxo. Mantém o contador.
  if (!isPageReload() || (state.screenMounts[screenId] ?? 0) === 0) {
    state.screenMounts[screenId] = (state.screenMounts[screenId] ?? 0) + 1;
  }
  writeState(state);
  emit("login_flow_screen_mounted", {
    screen_id: screenId,
    mount_count: state.screenMounts[screenId],
    route_changes: state.routeChanges,
    elapsed_ms: Date.now() - state.startedAt,
  });
};

export const recordRouteChange = (pathname: string) => {
  const inFlow = isInLoginFlow(pathname);
  let state = readState();

  if (inFlow && !state) {
    state = initialState();
    state.lastRoute = pathname;
    state.routeHistory.push(pathname);
    writeState(state);
    emit("login_flow_started", { route: pathname });
    return;
  }

  if (!state) return;

  if (state.lastRoute === pathname) return;

  state.routeChanges += 1;
  state.lastRoute = pathname;
  state.routeHistory.push(pathname);
  // Limita histórico para evitar crescimento ilimitado
  if (state.routeHistory.length > 25) {
    state.routeHistory = state.routeHistory.slice(-25);
  }
  writeState(state);

  emit("login_flow_route_changed", {
    route: pathname,
    route_changes: state.routeChanges,
    history: state.routeHistory,
    elapsed_ms: Date.now() - state.startedAt,
  });

  if (!inFlow) {
    emit("login_flow_ended", {
      final_route: pathname,
      route_changes: state.routeChanges,
      screen_mounts: state.screenMounts,
      history: state.routeHistory,
      elapsed_ms: Date.now() - state.startedAt,
    });
    writeState(null);
  }
};

export const getLoginFlowSnapshot = () => readState();
export const resetLoginFlowTelemetry = () => writeState(null);
